import { Check, MessageSquare, Paperclip, Trash2, Users } from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatRelative } from '@/utils/format-date'
import type { Bloco } from '@/types/bloco'
import { BlocoLabelChip } from './BlocoLabelChip'
import { formatBlocoDueLabel, formatMemberStatusLabel, summarizeSharedMemberStatus } from './blocosUtils'
import type { BlocoSectionKey } from './blocosUtils'

interface BlocoCardProps {
  bloco: Bloco
  sectionKey: BlocoSectionKey
  isOwner: boolean
  currentUserId?: string
  onOpen: (id: string) => void
  onComplete: (id: string) => void
  onDelete: (id: string) => void
}

export function BlocoCard({
  bloco,
  sectionKey,
  isOwner,
  currentUserId,
  onOpen,
  onComplete,
  onDelete,
}: BlocoCardProps) {
  const dueLabel = formatBlocoDueLabel(bloco)
  const isDone = bloco.status === 'done'
  const memberCount = bloco.members?.length ?? 0
  const isShared = bloco.isShared ?? memberCount > 1
  const messageCount = bloco.messageCount ?? 0
  const attachmentCount = bloco.attachmentCount ?? bloco.attachments?.length ?? 0
  const awaitingYou = currentUserId && bloco.pendingUserId === currentUserId
  const memberStatusSummary = summarizeSharedMemberStatus(bloco)
  const myMemberStatus = bloco.members?.find((member) => member.id === currentUserId)?.status

  return (
    <article
      className={cn(
        'bloco-card cursor-pointer transition-colors hover:border-[var(--brand-primary)]/30',
        sectionKey === 'awaitingYou' && 'bloco-card--today',
        sectionKey === 'overdue' && 'bloco-card--overdue',
        sectionKey === 'today' && 'bloco-card--today',
        isDone && 'bloco-card--done',
      )}
      onClick={() => onOpen(bloco.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onOpen(bloco.id)
        }
      }}
      role='button'
      tabIndex={0}
    >
      <p
        className={cn(
          'text-sm font-semibold leading-snug',
          isDone ? 'text-text-muted line-through' : 'text-text-primary',
        )}
      >
        {bloco.title || bloco.subject}
      </p>
      {bloco.subject && bloco.title !== bloco.subject && (
        <p className={cn('mt-1 text-xs text-text-muted', isDone && 'line-through')}>
          {bloco.subject}
        </p>
      )}
      {bloco.body?.trim() && (
        <p
          className={cn(
            'bloco-card__body mt-2 text-sm leading-relaxed',
            isDone ? 'text-text-muted line-through' : 'text-text-primary/80',
          )}
        >
          {bloco.body}
        </p>
      )}

      {bloco.labels && bloco.labels.length > 0 && (
        <div className='mt-2 flex flex-wrap gap-1'>
          {bloco.labels.map((label) => (
            <BlocoLabelChip key={label.id} label={label} className='text-[10px]' />
          ))}
        </div>
      )}

      <div className='mt-2 flex flex-wrap items-center justify-between gap-2'>
        <div className='flex flex-wrap items-center gap-2 text-[11px] text-text-muted'>
          {awaitingYou && !isDone && (
            <span className='rounded-md bg-amber-500/15 px-2 py-0.5 font-semibold text-amber-600'>
              Aguardando você
            </span>
          )}
          {bloco.pendingUser && !awaitingYou && !isDone && isShared && (
            <span className='rounded-md bg-[var(--glass-bg)] px-2 py-0.5 font-medium'>
              Com {bloco.pendingUser.name}
            </span>
          )}
          {dueLabel && (
            <span className='rounded-md bg-[var(--glass-bg)] px-2 py-0.5 font-medium'>
              {dueLabel}
            </span>
          )}
          {bloco.type === 'return' && !isDone && (
            <span className='rounded-md bg-[var(--brand-primary-muted)]/50 px-2 py-0.5 font-medium text-[var(--brand-primary)]'>
              Retorno
            </span>
          )}
          {isShared && (
            <span className='inline-flex items-center gap-1 rounded-md bg-[var(--glass-bg)] px-2 py-0.5 font-medium'>
              <Users size={12} />
              {memberCount}
            </span>
          )}
          {memberStatusSummary && (
            <span className='rounded-md bg-[var(--glass-bg)] px-2 py-0.5 font-medium'>
              {memberStatusSummary}
            </span>
          )}
          {myMemberStatus && isShared && (
            <span className='rounded-md bg-[var(--glass-bg)] px-2 py-0.5 font-medium'>
              Você: {formatMemberStatusLabel(myMemberStatus)}
            </span>
          )}
          {attachmentCount > 0 && (
            <span className='inline-flex items-center gap-1 rounded-md bg-[var(--glass-bg)] px-2 py-0.5 font-medium'>
              <Paperclip size={12} />
              {attachmentCount}
            </span>
          )}
          {messageCount > 0 && (
            <span className='inline-flex items-center gap-1 rounded-md bg-[var(--glass-bg)] px-2 py-0.5 font-medium'>
              <MessageSquare size={12} />
              {messageCount}
            </span>
          )}
          {bloco.author && !isOwner && (
            <span className='rounded-md bg-[var(--glass-bg)] px-2 py-0.5 font-medium'>
              de {bloco.author.name}
            </span>
          )}
          <span>{formatRelative(bloco.createdAt)}</span>
        </div>

        <div className='flex items-center gap-1' onClick={(event) => event.stopPropagation()}>
          {!isDone && (
            <button
              type='button'
              onClick={() => onComplete(bloco.id)}
              className='flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-emerald-500/10 hover:text-emerald-500'
              aria-label='Concluir bloco'
            >
              <Check size={16} />
            </button>
          )}
          {isOwner && (
            <button
              type='button'
              onClick={() => onDelete(bloco.id)}
              className='flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-red-500/10 hover:text-red-500'
              aria-label='Excluir bloco'
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
    </article>
  )
}
